/**
 * Self-healing for stale JS chunks after a deploy.
 *
 * When a user has the app open across a deploy, the hashed chunk files they
 * were served no longer exist. Navigating to a lazily-loaded route then fails
 * with "Importing a module script failed" / "Failed to fetch dynamically
 * imported module". The fix is a guarded full page reload which pulls the
 * fresh index.html + chunk manifest. The reload keeps the current URL and
 * query string intact (critical for /parent/checkout/success?session_id=...).
 */

const RELOAD_FLAG = "chunk-recovery:reloaded";
// Minimum time between automatic reloads. Allows recovery after every deploy
// while preventing a reload loop if a deploy is persistently broken.
const RELOAD_COOLDOWN_MS = 30_000;

const CHUNK_ERROR_PATTERNS = [
  "importing a module script failed",
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "'text/html' is not a valid javascript mime type",
  "expected a javascript module script",
  "unable to preload css",
  "loading chunk",
  "loading css chunk",
];

export function isChunkLoadError(error: unknown): boolean {
  const message =
    typeof error === "string"
      ? error
      : error && typeof error === "object" && "message" in error
        ? String((error as { message?: unknown }).message ?? "")
        : "";
  if (!message) return false;
  const lower = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((p) => lower.includes(p));
}

/**
 * Reload the current URL (path + query + hash preserved), at most once per
 * cooldown window, so a persistently broken deploy can't loop forever.
 * Returns true if a reload was triggered.
 */
export function reloadOnceForStaleChunks(reason = "stale-chunk"): boolean {
  if (typeof window === "undefined") return false;
  try {
    const lastReload = Number(window.sessionStorage.getItem(RELOAD_FLAG) ?? 0);
    if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
      console.warn(`[chunk-recovery] reloaded recently; not reloading again (${reason})`);
      return false;
    }
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode / blocked) — don't risk a loop.
    return false;
  }
  console.warn(`[chunk-recovery] stale chunk detected (${reason}); reloading ${window.location.href}`);
  // reload() preserves pathname, search and hash exactly.
  window.location.reload();
  return true;
}

let installed = false;

/** Install global listeners for Vite preload failures and dynamic-import rejections. */
export function installChunkRecovery() {
  if (typeof window === "undefined" || installed) return;
  installed = true;

  // Vite fires this when a modulepreload for a dynamic import fails.
  // Do NOT call event.preventDefault(): it makes Vite resolve the import as
  // `undefined`, and TanStack Router then crashes with
  // "undefined is not an object (evaluating 'e[t ?? default]')" instead of
  // surfacing the real chunk-load error that the error boundary can recognise.
  window.addEventListener("vite:preloadError", () => {
    reloadOnceForStaleChunks("vite:preloadError");
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      reloadOnceForStaleChunks("unhandledrejection");
    }
  });

  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error ?? event.message)) {
      reloadOnceForStaleChunks("window.error");
    }
  });
}
